import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ChangePasswordRequest, UpdateProfileRequest } from '../types';
import { Eye, EyeOff, Save } from 'lucide-react';

export const ProfilePage: React.FC = () => {
  const { user, updateProfile, changePassword, loading, error, clearError } = useAuth();
  const [profileData, setProfileData] = useState<UpdateProfileRequest>({
    username: user?.username || '',
    email: user?.email || '',
  });
  const [passwordData, setPasswordData] = useState<ChangePasswordRequest>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const validateProfile = (): boolean => {
    const errors: Record<string, string> = {};
    if (!profileData.username?.trim()) {
      errors.username = 'Username is required';
    } else if (profileData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(profileData.username)) {
      errors.username = 'Username can only contain letters, numbers, and underscores';
    }
    if (!profileData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePassword = (): boolean => {
    const errors: Record<string, string> = {};
    if (!passwordData.current_password) {
      errors.current_password = 'Current password is required';
    }
    if (!passwordData.new_password) {
      errors.new_password = 'New password is required';
    } else if (passwordData.new_password.length < 8) {
      errors.new_password = 'Password must be at least 8 characters';
    }
    if (!passwordData.confirm_password) {
      errors.confirm_password = 'Please confirm your new password';
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setProfileSuccess(false);
    if (!validateProfile()) return;
    try {
      await updateProfile(profileData);
      setProfileSuccess(true);
    } catch {}
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setPasswordSuccess(false);
    if (!validatePassword()) return;
    try {
      await changePassword(passwordData);
      setPasswordSuccess(true);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch {}
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (['username', 'email'].includes(name)) {
      setProfileData(prev => ({ ...prev, [name]: value }));
    } else {
      setPasswordData(prev => ({ ...prev, [name]: value }));
    }
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-base-100 rounded-lg shadow-lg mt-8">
      <h2 className="text-2xl font-bold mb-6">My Profile</h2>
      {/* Profile Update */}
      <form onSubmit={handleProfileSubmit} className="space-y-4 mb-8">
        <div className="form-control">
          <label className="label">Username</label>
          <input
            type="text"
            name="username"
            value={profileData.username}
            onChange={handleInputChange}
            className={`input input-bordered w-full ${validationErrors.username ? 'input-error' : ''}`}
            disabled={loading}
          />
          {validationErrors.username && <span className="text-error text-xs">{validationErrors.username}</span>}
        </div>
        <div className="form-control">
          <label className="label">Email</label>
          <input
            type="email"
            name="email"
            value={profileData.email}
            onChange={handleInputChange}
            className={`input input-bordered w-full ${validationErrors.email ? 'input-error' : ''}`}
            disabled={loading}
          />
          {validationErrors.email && <span className="text-error text-xs">{validationErrors.email}</span>}
        </div>
        <button type="submit" className={`btn btn-primary w-full ${loading ? 'loading' : ''}`} disabled={loading}>
          <Save className="w-4 h-4 mr-2" /> Save Profile
        </button>
        {profileSuccess && <div className="alert alert-success mt-2">Profile updated!</div>}
      </form>
      {/* Password Change */}
      <form onSubmit={handlePasswordSubmit} className="space-y-4">
        <h3 className="text-lg font-semibold mb-2">Change Password</h3>
        <div className="form-control">
          <label className="label">Current Password</label>
          <div className="relative">
            <input
              type={showPasswords.current ? 'text' : 'password'}
              name="current_password"
              value={passwordData.current_password}
              onChange={handleInputChange}
              className={`input input-bordered w-full pr-12 ${validationErrors.current_password ? 'input-error' : ''}`}
              disabled={loading}
            />
            <button type="button" onClick={() => togglePasswordVisibility('current')} className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {validationErrors.current_password && <span className="text-error text-xs">{validationErrors.current_password}</span>}
        </div>
        <div className="form-control">
          <label className="label">New Password</label>
          <div className="relative">
            <input
              type={showPasswords.new ? 'text' : 'password'}
              name="new_password"
              value={passwordData.new_password}
              onChange={handleInputChange}
              className={`input input-bordered w-full pr-12 ${validationErrors.new_password ? 'input-error' : ''}`}
              disabled={loading}
            />
            <button type="button" onClick={() => togglePasswordVisibility('new')} className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {validationErrors.new_password && <span className="text-error text-xs">{validationErrors.new_password}</span>}
        </div>
        <div className="form-control">
          <label className="label">Confirm New Password</label>
          <div className="relative">
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              name="confirm_password"
              value={passwordData.confirm_password}
              onChange={handleInputChange}
              className={`input input-bordered w-full pr-12 ${validationErrors.confirm_password ? 'input-error' : ''}`}
              disabled={loading}
            />
            <button type="button" onClick={() => togglePasswordVisibility('confirm')} className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {validationErrors.confirm_password && <span className="text-error text-xs">{validationErrors.confirm_password}</span>}
        </div>
        <button type="submit" className={`btn btn-primary w-full ${loading ? 'loading' : ''}`} disabled={loading}>
          <Save className="w-4 h-4 mr-2" /> Change Password
        </button>
        {passwordSuccess && <div className="alert alert-success mt-2">Password changed!</div>}
      </form>
      {error && <div className="alert alert-error mt-4">{error}</div>}
    </div>
  );
}; 